import {
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    inject,
    Inject,
    OnInit,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {BehaviorSubject, forkJoin, of, throwError} from 'rxjs';
import {catchError, debounceTime, map, take, tap} from 'rxjs/operators';
import {TreeNode} from '@ali-hm/angular-tree-component';
import {NotificationService} from '@emm/shared/services/notification.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MenuKeyContext} from '@emm/core/models/menu-key.model';
import {Submenu} from '@emm/core/models/submenu.model';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {MenuService} from '@emm/core/services/menu.service';
import {wrap} from '@emm/core/util/rx.util';
import {MenuStoreService} from '@emm/menu/services/menu-store.service';
import {MenuTreeFacadeService} from '@emm/menu/services/menu-tree-facade.service';
import {MenuApiService} from '@emm/menu/services/menu-api.service';
import {MenuFormFacadeService} from '@emm/menu/services/menu-form-facade.service';

@Component({
    selector: 'emm-update-cascade-item-dialog',
    styleUrls: ['./update-cascade-item-dialog.component.scss'],
    templateUrl: './update-cascade-item-dialog.component.html'
})
export class UpdateCascadeItemDialogComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    isLinear = true;
    featureScope: string;
    firstFormGroup: FormGroup = new FormGroup<any>({});
    secondFormGroup: FormGroup = new FormGroup<any>({});
    menuKey: MenuKeyContext;
    matchCount: number;
    spinner = false;
    color = '#e5173f';
    mode = 'indeterminate';
    diameter = 25;
    response;
    subMenu;
    topChips = [];
    bottomChips = [];
    selectedBottomChips = [];
    topChipsChanged$ = new EventEmitter();
    bottomChipsChanged$ = new EventEmitter();
    pathChanged$ = new EventEmitter();
    path;
    showPath = false;
    updates = [];
    selectedSubmenus: string[] = [];
    submenus: Array<Submenu>;
    currentSubmenu;
    forceOverrides = this.data.forceOverrides;
    locationSet = this.data.locationSet;
    effectiveDate = this.data.effectiveDate;
    removedMessage = 'Removed';
    itemDetail: ItemDetail;

    @ViewChild('cascadeCheckbox') cascadeCheckbox: ElementRef;
    checkbox = new FormControl('');
    filterValue = '';
    filterSize: number;
    formattedOverrides = [];
    displayedColumns: string[] = [];
    forceOverridesInheritanceMap: Record<string, boolean> = {};
    dataSource = new BehaviorSubject<any[]>([]);
    newUpdateFlow$ = this.formFacade.newUpdateFlow$;
    useNewFlow = new BehaviorSubject<boolean>(false);
    isLoading = new BehaviorSubject<boolean>(true);

    constructor(
        private formBuilder: FormBuilder,
        public dialogRef: MatDialogRef<UpdateCascadeItemDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private menuService: MenuService,
        private menuStore: MenuStoreService,
        private notificationService: NotificationService,
        private treeFacade: MenuTreeFacadeService,
        private api: MenuApiService,
        private formFacade: MenuFormFacadeService,
    ) {
        this.updates = data.updates;
        this.itemDetail = data.itemDetail;
        this.menuKey = data.menuKey;
        this.featureScope = this.data.featureScope;
    }


    ngOnInit(): void {
        this.menuStore.querySubmenus.selected$.subscribe(result => this.currentSubmenu = result);
        this.treeFacade.resetCascadeSubmenus();
        this.setSubmenus();
        this.initChips();
        this.generatePath();

        this.firstFormGroup = this.formBuilder.group({
            checkbox: this.checkbox
        });
        this.secondFormGroup = this.formBuilder.group({});

        // simulate radio button where only 1 chip can be selected
        this.topChipsChanged$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((chip) => {
                const newValue = !chip.selected;
                chip.selected = newValue;
                this.topChips.filter((ch) => chip.tag !== ch.tag).forEach((ch) => (ch.selected = !newValue));
                this.subMenu = this.topChips.filter((ch) => ch.tag !== 'ROOT').filter((ch) => ch.selected)[0];
                this.pathChanged$.emit();
            });

        this.bottomChipsChanged$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((chip) => {
            // validate deselecting bottom chip (item)
            if (chip.tag === this.data.item.data.data.tag) {
                this.notificationService.error('Cannot deselect item to be updated');
                return;
            }

            // toggle selected chip
            const newValue = !chip.selected;

            // toggle descendant chips
            chip.selected = newValue;
            if (newValue) {
                // set all child descendants to true
                const match = this.bottomChips.indexOf(chip);
                this.bottomChips.filter((ch, index) => index > match).forEach((ch) => (ch.selected = true));
            }

            // Check if the deselection creates 2 or more gaps
            const selectedIndexes = this.bottomChips
                .map((ch, index) => (ch.selected ? index : -1))
                .filter(index => index !== -1);

            const sortedIndexes = selectedIndexes.sort((a, b) => a - b);
            const gapCount = sortedIndexes.reduce((count, index, i) => {
                if (i < sortedIndexes.length - 1 && sortedIndexes[i + 1] - index > 1) {
                    return count + 1;
                }
                return count;
            }, 0);

            if (gapCount > 1) {
                // Revert the deselection
                chip.selected = !chip.selected;
                this.notificationService.error('Cannot create 2 or more gaps in selected chips');
            }

            this.pathChanged$.emit();
        });

        // update json path
        this.pathChanged$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
            this.generatePath();
        });
        this.newUpdateFlow$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
            if (result) {
                this.setDisplayedColumns();
                this.formatDataSource();
            }
            this.useNewFlow.next(result);
            this.isLoading.next(false);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['locationSet']) {
            this.setDisplayedColumns();
        }
    }

    setDisplayedColumns(): void {
        this.displayedColumns = ['attribute', 'locations', 'currentValue', 'newValue'];
        if (this.locationSet !== 'Location') {
            this.displayedColumns.push('forceOverrides');
        }
    }

    forceOverrideForAttribute(row: any) {
        row.forceOverrides = !row?.forceOverrides;
        if (row?.forceOverrides) {
            this.forceOverridesInheritanceMap[row.attribute] = true;
        } else {
            delete this.forceOverridesInheritanceMap[row.attribute];
        }
    }

    formatDataSource() {
        if (!this.itemDetail?.tagPath) return;
        const updateKeys = Object.keys(this.updates);
        const tagPathArray = this.itemDetail?.tagPath?.split('/');
        tagPathArray?.shift();
        tagPathArray?.shift();
        const itemPath = tagPathArray?.join('/');

        const requests = updateKeys.map(attribute =>
            this.api.getAttributeOverrides(attribute, this.currentSubmenu, `/${itemPath}`, this.effectiveDate)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    map(res => ({ attribute, res })),
                    catchError(() => of({ attribute, res: null }))
            )
        );

        forkJoin(requests).subscribe(results => {
            this.formattedOverrides = [];
            const tableRows = results.map(({ attribute, res }) => {
                const overrides = res
                    ? Object.entries(res).map(([key, value]) => ({ key, value }))
                    : [];

                this.formattedOverrides.push(...overrides);

                return {
                    attribute,
                    locations: overrides.length ? overrides.map(o => o.key).join(',\n') : 'None',
                    currentValue: this.itemDetail?.attributes.find(a => a.jsonProperty === attribute)?.value,
                    newValue: this.updates[attribute],
                    forceOverrides: false
                };
            });

            this.dataSource.next(tableRows);
        });
    }

    initChips(): void {
        // find item hierarchy
        const nodes = [];
        nodes.push(this.createChip(this.data?.item?.data?.data, true));
        this.findParents(this.data?.item).forEach((parent) =>
            nodes.push(this.createChip(parent?.data?.data, false))
        );
        const chips = nodes.reverse();

        // split item path to top-level (root / submenu) and bottom-level (parent ancestors)
        const rootChipName = 'Submenu';
        this.topChips.push({
            name: rootChipName,
            selected: true,
            tag: 'ROOT'
        });
        this.bottomChips = chips.slice(1);
    }

    findParents(item: TreeNode): TreeNode[] {
        return this.findParent(item, []);
    }

    findParent(node: TreeNode, parents: TreeNode[]): TreeNode[] {
        const parent = node?.parent;
        if (parent && !node.isRoot) {
            parents.push(parent);
            return this.findParent(parent, parents);
        }
        return parents;
    }

    createChip({name = '', tag = ''} = {}, selected: boolean): {} {
        return {name, selected, tag};
    }

    toggleSelectBottomChip(chip): void {
        this.bottomChipsChanged$.emit(chip);
    }

    generatePath(): void {
        this.path = null;
        const selectedBottomChips = this.bottomChips.filter((chip => chip.selected));
        this.selectedBottomChips = selectedBottomChips;
        this.path = this.getPath(selectedBottomChips);
        setTimeout(() => {
            this.findMatches(this.featureScope, this.path)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe();
        }, 1);
    }

    getPath(selectedBottomChips): object | string {
        let fullTagPath = '';
        let ancestorMatchPath = '';
        let itemMatchPath = '';

        // more than 2 selected groups of chips is not allowed,
        if (selectedBottomChips.length === this.bottomChips.length) {
            for (const chip of selectedBottomChips) {
                fullTagPath += `/${chip.tag}`;
            }
            return {itemMatchPath: fullTagPath};
        } else {
            for (const chip of selectedBottomChips) {
                // if consecutive, add to ancestor match path
                if (selectedBottomChips.indexOf(chip) === this.bottomChips.indexOf(chip)) {
                    ancestorMatchPath += `/${chip.tag}`;
                }
                // if not consecutive, add to item match path
                else {
                    if (selectedBottomChips.length > 1) {
                        ancestorMatchPath.includes(selectedBottomChips[0].tag) ? ancestorMatchPath += `` :
                            ancestorMatchPath += `/${selectedBottomChips[0].tag}`;

                        for (let i = 1; i < selectedBottomChips.length; i++) {
                            itemMatchPath.includes(selectedBottomChips[i].tag) ||
                            ancestorMatchPath.includes(selectedBottomChips[i].tag) ? itemMatchPath += `` :
                                itemMatchPath += `/${selectedBottomChips[i].tag}`;
                        }
                    } else {
                        itemMatchPath += `/${chip.tag}`;
                    }
                }
            }
            return {
                ancestorMatchPath,
                itemMatchPath
            };
        }
    }

    findMatches(featureScope, path): any { // todo: type

        const before = () => {
            this.spinner = true;
        };
        if (!this.menuKey?.submenu) {
            const submenu = this.getSearchSubmenu();
            if (!this.selectedSubmenus.includes(submenu) && submenu !== null) {
                this.treeFacade.selectCascadeSubmenus(submenu);
                this.refreshCascadeSubmenus();
            }
        }
        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            const source$ = this.menuService
                .countMatchesForUpdateCascade(featureScope, this.data.effectiveDate, this.menuKey, path, this.selectedSubmenus)
                .pipe(
                    tap((results) => {
                        this.spinner = false;
                        this.matchCount = results;
                    }),
                    catchError(() => {
                        const errorMessage = 'Error searching matches';
                        this.notificationService.error(errorMessage);
                        return throwError(errorMessage);
                    })
                );

            return wrap({
                before,
                source$
            });
        }
    }


    togglePath(): void {
        this.showPath = !this.showPath;
    }


    onSave(): void {
        if (!this.menuKey?.submenu) {
            const submenu = this.getSearchSubmenu();
            if (!this.selectedSubmenus.includes(submenu) && submenu !== null) {
                this.treeFacade.selectCascadeSubmenus(submenu);
                this.refreshCascadeSubmenus();
            }
        }
        const checkbox = this.cascadeCheckbox ? this.cascadeCheckbox['checked'] : false;
        if (this.useNewFlow.value === false) {
            this.formFacade.updateMenuItemCascade(this.itemDetail.tagPath, this.path, this.updates, checkbox);
            this.dialogRef.close({path: this.path, isChecked: checkbox});
        } else {
            this.formFacade.updateMenuItemCascade(this.itemDetail.tagPath, this.path, this.updates, checkbox,
                this.forceOverridesInheritanceMap);
            this.dialogRef.close({path: this.path, isChecked: checkbox});
        }
    }


    onNoClick(): void {
        this.dialogRef.close();
    }

    refreshCascadeSubmenus() {
        this.treeFacade.selectedSubmenusUpdate$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
            for (const submenu of result) {
                if (this.selectedSubmenus.includes(submenu) && submenu) {
                    this.selectedSubmenus.filter(element => element !== submenu);
                } else {
                    this.selectedSubmenus.push(submenu);
                }
            }});
    }


    deselectCascadeSubmenus(submenuTag: string) {
        if (this.selectedSubmenus.includes(submenuTag)) {
            this.selectedSubmenus.splice(this.selectedSubmenus.indexOf((submenuTag)));
        }
    }

    updateAllSelected(checked, checkedValue) {
        if (!(this.selectedSubmenus.includes(this.currentSubmenu))) {
            this.treeFacade.selectCascadeSubmenus(this.currentSubmenu);
        }

        this.spinner = true;
        if (checked) {
            this.treeFacade.selectCascadeSubmenus(checkedValue);
            this.refreshCascadeSubmenus();
        } else {
            this.treeFacade.deselectCascadeSubmenus(checkedValue);
            this.deselectCascadeSubmenus(checkedValue);
        }
        if (!this.menuKey?.submenu) {
            const submenu = this.getSearchSubmenu();
            if (!this.selectedSubmenus.includes(submenu) && submenu !== null) {
                this.treeFacade.selectCascadeSubmenus(submenu);
                this.refreshCascadeSubmenus();
            }
        }
        if (this.featureScope === 'Global' || this.featureScope === 'Viewer') {
            this.menuService.countMatchesForUpdateCascade(this.featureScope, this.data.effectiveDate, this.menuKey,
                this.path, this.selectedSubmenus)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(results => {
                this.matchCount = results;
                this.spinner = false;
                catchError(() => {
                    const errorMessage = 'Error searching parent nodes';
                    this.notificationService.error(errorMessage);
                    return throwError(errorMessage);
                });
            });
        }
    }

    private setSubmenus(filterStr?: string): void {
        this.api.getSubmenusByFeatureScope(this.featureScope, this.menuKey)
            .pipe(
                debounceTime(300),
                map((result) => {
                    if (filterStr) {
                        return result.filter((item) => item.name.toLowerCase().includes(filterStr)
                            || item.id.toLowerCase().includes(filterStr));
                    } else {
                        return result;
                    }
                }),
                map((submenus) => this.submenus = submenus),
                tap((submenus) => this.filterSize = submenus.length),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    onFilterChange($event: Event) {
        const filterStr = ($event.target as HTMLInputElement).value;
        this.filterSubmenus(filterStr.toLowerCase());
    }

    getSearchSubmenu(): string {
        let submenu = null;
        this.menuStore.querySubmenus.selectedSearchSubmenu$.pipe(take(1)).subscribe(value =>
            submenu = value);
        return submenu;
    }

    private filterSubmenus(value: string): void {
        this.setSubmenus(value?.toLowerCase());
    }

    public clearFilter() {
        this.filterValue = '';
        this.setSubmenus();
    }
}
